import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import {
  allPageOrigins,
  contentHeight,
  contentWidth,
  type LayoutLike,
  type Point,
  type RectLike,
} from './coords';

export interface ViewerLayoutState {
  ready: boolean;
  scale: number;
  layout: LayoutLike | null;
  pages: Record<number, RectLike>;
  scroll: Point;
  offset: Point;
}

interface Capabilities {
  scroll: {
    getLayout(): { virtualItems: unknown[] };
    scrollToPage(options: { pageNumber: number }): void;
    onLayoutChange(cb: (layout: unknown) => void): () => void;
  };
  zoom: {
    getState(): { currentZoomLevel: number };
    onStateChange(cb: (state: { currentZoomLevel: number }) => void): () => void;
  };
}

function findScroller(host: HTMLElement | null): HTMLElement | null {
  if (!host?.shadowRoot) return null;
  const candidates = Array.from(host.shadowRoot.querySelectorAll('div')).filter((el) => {
    const style = window.getComputedStyle(el);
    return style.overflowY === 'auto' || style.overflowY === 'scroll';
  });
  let best: HTMLElement | null = null;
  for (const el of candidates) {
    if (!best || el.scrollHeight > best.scrollHeight) best = el;
  }
  return best;
}

/**
 * Binds the embedpdf viewer (layout at scale 1, zoom level, scroller offsets)
 * into a live state object the overlay can render against.
 */
export function usePdfLayout(
  wrapperRef: RefObject<HTMLDivElement | null>,
  resetKey: string | null
): ViewerLayoutState & {
  handleViewerReady: (registry: unknown) => void;
  scrollToPage: (page: number) => void;
  getScroller: () => HTMLElement | null;
} {
  const [state, setState] = useState<ViewerLayoutState>({
    ready: false,
    scale: 1,
    layout: null,
    pages: {},
    scroll: { x: 0, y: 0 },
    offset: { x: 0, y: 0 },
  });
  const capsRef = useRef<Capabilities | null>(null);
  const unsubsRef = useRef<Array<() => void>>([]);
  const scrollerRef = useRef<HTMLElement | null>(null);

  const refreshAll = useCallback(() => {
    const caps = capsRef.current;
    const scroller = findScroller(
      wrapperRef.current?.querySelector('embedpdf-container') as HTMLElement | null
    );
    const wrapper = wrapperRef.current;
    if (!caps || !scroller || !wrapper) return;
    scrollerRef.current = scroller;
    const layout = caps.scroll.getLayout() as unknown as LayoutLike;
    if (!layout?.virtualItems?.length) return;
    const scale = caps.zoom.getState().currentZoomLevel;
    const wrapperRect = wrapper.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();
    const centeringX = Math.max(0, (scroller.clientWidth - contentWidth(layout) * scale) / 2);
    const spacingY = Math.max(0, (scroller.clientHeight - contentHeight(layout) * scale) / 2);
    setState((prev) => ({
      ...prev,
      ready: true,
      layout,
      pages: allPageOrigins(layout),
      scale,
      scroll: { x: scroller.scrollLeft, y: scroller.scrollTop },
      offset: {
        x: scrollerRect.left - wrapperRect.left + scroller.clientLeft + centeringX,
        y: scrollerRect.top - wrapperRect.top + scroller.clientTop + spacingY,
      },
    }));
  }, [wrapperRef]);

  const teardown = useCallback(() => {
    unsubsRef.current.forEach((fn) => {
      try {
        fn();
      } catch {
        /* viewer already gone */
      }
    });
    unsubsRef.current = [];
    capsRef.current = null;
    scrollerRef.current = null;
  }, []);

  useEffect(() => {
    setState({ ready: false, scale: 1, layout: null, pages: {}, scroll: { x: 0, y: 0 }, offset: { x: 0, y: 0 } });
    teardown();
    return teardown;
  }, [resetKey, teardown]);

  const handleViewerReady = useCallback(
    (registry: unknown) => {
      teardown();
      const reg = registry as {
        getPlugin: (name: string) => { provides: () => unknown } | null | undefined;
      };
      if (!reg?.getPlugin) return;
      const scrollPlugin = reg.getPlugin('scroll') as unknown as {
        provides(): Capabilities['scroll'];
      } | null;
      const zoomPlugin = reg.getPlugin('zoom') as unknown as {
        provides(): Capabilities['zoom'];
      } | null;
      if (!scrollPlugin || !zoomPlugin) return;
      const caps = { scroll: scrollPlugin.provides(), zoom: zoomPlugin.provides() };
      capsRef.current = caps;

      unsubsRef.current.push(caps.zoom.onStateChange(() => requestAnimationFrame(refreshAll)));
      unsubsRef.current.push(
        (
          caps.scroll as unknown as {
            onLayoutChange(cb: () => void): () => void;
          }
        ).onLayoutChange(() => requestAnimationFrame(refreshAll))
      );

      let attempts = 0;
      const poll = window.setInterval(() => {
        attempts += 1;
        const scroller = findScroller(
          wrapperRef.current?.querySelector('embedpdf-container') as HTMLElement | null
        );
        const hasPages = !!caps.scroll.getLayout?.().virtualItems?.length;
        if (scroller && hasPages) {
          window.clearInterval(poll);
          scrollerRef.current = scroller;
          scroller.addEventListener('scroll', () => {
            setState((prev) => ({ ...prev, scroll: { x: scroller.scrollLeft, y: scroller.scrollTop } }));
          });
          refreshAll();
        } else if (attempts > 240) {
          window.clearInterval(poll);
        }
      }, 250);
      unsubsRef.current.push(() => window.clearInterval(poll));
    },
    [refreshAll, teardown, wrapperRef]
  );

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const observer = new ResizeObserver(() => refreshAll());
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [wrapperRef, refreshAll, state.ready]);

  const scrollToPage = useCallback((page: number) => {
    capsRef.current?.scroll.scrollToPage({ pageNumber: page });
  }, []);

  const getScroller = useCallback(() => scrollerRef.current, []);

  return { ...state, handleViewerReady, scrollToPage, getScroller };
}
